---
title: Arrangement
description: "A DAW's arrangement: a zoomable timeline with a ruler that seeks and lanes of clips, and the parts around it, its state, clips, tracks, locators, automation and MIDI playback, each installed on its own."
component: true
base: base
---

<ComponentPreview styleName="base-nova" name="block-timeline" />

An arrangement is tracks of clips along a song. It comes in two layers that install together and apart. The **timeline** draws and moves the clips on a zoomable ruler: lanes, ranges, a playhead and a navigator, with no notion of tempo. The **arrangement parts** hold what a DAW's arrangement needs around it: the state, the clips, track reordering, locators and time signatures, automation and MIDI playback. Each is a file you own, takes its state as values and reports changes through callbacks, so the arrangement can live in your own store and play through an engine of your own.

## Installation

The whole arrangement installs with `npx shadcn@latest add @audio-ui/arrangement-editor`: every part below, with the timeline. A part installs alone by its name (`@audio-ui/arranged-clip`, `@audio-ui/use-track-reorder`…). The preview above is `@audio-ui/block-timeline`, which wires them into a screen. The tabs below are the timeline on its own.


<CodeTabs>

<TabsList>
  <TabsTrigger value="cli"><Icons.shadcn className="size-3.5" /> CLI</TabsTrigger>
  <TabsTrigger value="manual"><Icons.react className="size-3.5" /> Manual</TabsTrigger>
  <TabsTrigger value="agent"><Icons.sparkle className="size-3.5" /> Agent</TabsTrigger>
</TabsList>

<TabsContent value="cli">

```bash component
npx shadcn@latest add @audio-ui/timeline
```

</TabsContent>

<TabsContent value="manual">

<Steps>

<Step>Install the following dependencies:</Step>

```bash
npm install @audio-ui/react
```

<Step>Copy and paste the following code into your project.</Step>

<ComponentSource
  styleName="base-nova"
  name="timeline"
  title="components/audio-ui/timeline.tsx"
/>

<Step>Update the import paths to match your project setup.</Step>

</Steps>

</TabsContent>

<TabsContent value="agent">

<InstallAgent name="arrangement-editor" />

</TabsContent>

</CodeTabs>

## The timeline

<ComponentPreview styleName="base-nova" name="timeline-clips-demo" />

The preview is the timeline alone: a loop, a ruler and two tracks with clips. A clip let go over another covers it, as `coverClips` does (see Clips below): drop Fill inside Groove and Groove is cut in two. The registry component wraps the `Timeline` primitive from `@audio-ui/react`. The timeline has no notion of tempo: its units are whatever you count in (seconds, beats, pulses or steps). Musical time comes from the `musical-time` lib.

### Overview

- **Units and zoom:** `duration` is the length in your units, and `zoom` is the number of pixels per unit. Without `zoom`, the timeline fits its width. With it, every lane is as long as the timeline and the viewport scrolls.
- **Zooming:** Cmd/Ctrl + scroll, or a trackpad pinch, zooms around the pointer. Every zoom change keeps its anchor point in place, or the middle of the view when there is none.
- **Grid:** with a `snapStep`, the ruler snaps to the grid. Hold Cmd/Ctrl to place the playhead off the grid, and Shift to scrub four times finer.
- **Playback:** pass a live `source` and every playhead follows it in the DOM, without a React render per frame.
- **Ranges:** a `TimelineRange` is a stretch of the timeline, such as a loop, a selection or a clip. Drag it to move it, drag a handle to resize it, or, with `drawable`, drag across an empty lane to draw a new one. Ranges snap to the grid.
- **Navigator:** `TimelineNavigator` shows the whole timeline as one bar, with the visible part inside it. Drag the window to scroll, drag an edge to zoom, and double-click to show everything. With `scrollbar="hidden"` on the viewport, it replaces the scrollbar.
- **Accessibility:** the ruler, each range and range handle, and the navigator's window and edges hold a native `<input type="range">`, so every pointer action has a keyboard equivalent. The ruler's input spans the timeline: it takes focus, carries `aria-label` and announces the position (or the text from `getAriaValueText`, such as "Bar 3, beat 2"). The arrow keys move by `step`, <kbd>Page Up</kbd> / <kbd>Page Down</kbd> and Shift by `largeStep`, and <kbd>Home</kbd> / <kbd>End</kbd> go to the ends.

### Anatomy

Import the primitive and assemble its parts.

```tsx
import { Timeline } from "@audio-ui/react";

<Timeline.Root>
  <Timeline.Viewport>
    <Timeline.Ruler>
      <Timeline.Mark value={0} />
      <Timeline.Playhead />
    </Timeline.Ruler>
    <Timeline.Track>
      <Timeline.TrackHeader>
        <Timeline.TrackResizeHandle />
      </Timeline.TrackHeader>
      <Timeline.Lane />
    </Timeline.Track>
    <Timeline.Lane>
      <Timeline.Range>
        <Timeline.RangeHandle edge="start" />
        <Timeline.RangeHandle edge="end" />
      </Timeline.Range>
      <Timeline.Clip>
        <Timeline.ClipHeader />
        <Timeline.ClipFade edge="in" />
        <Timeline.ClipFade edge="out" />
        <Timeline.ClipHandle edge="start" />
        <Timeline.ClipHandle edge="end" />
        <Timeline.ClipFadeHandle edge="in" />
        <Timeline.ClipFadeHandle edge="out" />
      </Timeline.Clip>
      <Timeline.Playhead />
    </Timeline.Lane>
  </Timeline.Viewport>
  <Timeline.Navigator>
    <Timeline.NavigatorWindow />
    <Timeline.NavigatorHandle edge="start" />
    <Timeline.NavigatorHandle edge="end" />
  </Timeline.Navigator>
</Timeline.Root>
```

### Usage

```tsx
import {
  Timeline,
  TimelineLane,
  TimelineMark,
  TimelinePlayhead,
  TimelineRuler,
  TimelineViewport,
} from "@/components/audio-ui/timeline";
import {
  DEFAULT_TEMPO,
  describeBarsBeats,
  gridInterval,
} from "@audio-ui/core/time";
```

```tsx
const snapStep = gridInterval("adaptive", zoom, DEFAULT_TEMPO);

<Timeline
  duration={32}
  onPositionChange={(seconds) => transport.seek(seconds)}
  onZoomChange={setZoom}
  snapStep={snapStep}
  source={transport.position}
  zoom={zoom}
>
  <TimelineViewport>
    <TimelineRuler
      aria-label="Playhead"
      getAriaValueText={(seconds) => describeBarsBeats(seconds, DEFAULT_TEMPO)}
    >
      <TimelineMark value={0}>1</TimelineMark>
      <TimelineMark value={2}>2</TimelineMark>
      <TimelinePlayhead />
    </TimelineRuler>
    <TimelineLane gridStep={snapStep}>
      <TimelinePlayhead />
    </TimelineLane>
  </TimelineViewport>
</Timeline>
```

### Ranges

```tsx
const [loop, setLoop] = useState<readonly [number, number]>([8, 16]);

<TimelineLane>
  <TimelineRange aria-label="Loop" drawable onValueChange={setLoop} value={loop}>
    <TimelineRangeHandle aria-label="Loop start" edge="start" />
    <TimelineRangeHandle aria-label="Loop end" edge="end" />
  </TimelineRange>
</TimelineLane>
```

The range's own input moves it with Left and Right, by the timeline's `step`, and with Up and Down by its own length; Cmd/Ctrl + Left and Right shorten or lengthen it by a step, Cmd/Ctrl + Up and Down double or halve it; each handle's input moves its edge, which stops `minLength` short of the other one (the grid step by default). `onValueChange` receives `[start, end]` and `eventDetails` with the `edge` that moved (`"start"`, `"end"` or `"both"`) and the `reason` (`"drag"`, `"draw"`, `"keyboard"` or `"input-change"`). Leave the handles out for a range that only moves, or pass `movable={false}` for one that only resizes.

### Tracks

A track is a row: a header beside a lane. `Timeline.Track` holds the row, `Timeline.TrackHeader` is the strip that names it and stays at the left edge while the lane scrolls, and `Timeline.TrackResizeHandle` sets its height. You hold the height: leave it out and the row shares the room it is given; set it and the row keeps it.

```tsx
const [height, setHeight] = useState<number | undefined>();

<Timeline.Track
  height={height}
  onHeightChange={(next) => setHeight(next)}
  selected={selected}
>
  <Timeline.TrackHeader>
    Drums
    <Timeline.TrackResizeHandle aria-label="Height of Drums" />
  </Timeline.TrackHeader>
  <Timeline.Lane>{/* clips */}</Timeline.Lane>
</Timeline.Track>;
```

You put the header's controls in it, such as solo, mute and gain. The track does not know about them.

The block reorders tracks by their grip, the bar at the track's left edge: press it and drag. A copy of the track's header follows the pointer, the dragged track stays in place faded, and a line marks the place it would land in. Near the top or bottom of a view that scrolls, the view scrolls on its own while the track is held. The arrow keys move a focused grip one place up or down, and every move is one undo step with its clips. The move is `moveTrack(id, index)` in `useArrangement`; it uses a pointer drag rather than the browser's drag and drop, so it does not depend on how the browser handles a drag.

### Clips

A clip is a stretch of a source (a recording, a pattern) laid on a track. It is a range that knows about its source: trimming its start moves where in the source it begins, so its content stays where it is, and it cannot show more than the source holds.

```tsx
const [clip, setClip] = useState<Timeline.Clip.Value>({
  start: 8, // where it sits on the timeline
  length: 16, // how long it plays
  offset: 4, // where in the source it begins
  fadeIn: 0,
  fadeInShape: "linear", // or "fast", "slow", "smooth"
  fadeOut: 0,
});

<Timeline.Lane data-track="drums" seekable>
  <Timeline.Clip
    aria-label="Drums, Groove"
    onRemove={() => removeClip()}
    onValueChange={setClip}
    sourceLength={48}
    value={clip}
  >
    <Timeline.ClipHeader>Groove</Timeline.ClipHeader>
    <Timeline.ClipFade edge="in" />
    <Timeline.ClipFade edge="out" />
    <Timeline.ClipHandle aria-label="Groove start" edge="start" />
    <Timeline.ClipHandle aria-label="Groove end" edge="end" />
    <Timeline.ClipFadeHandle aria-label="Groove fade in" edge="in" />
    <Timeline.ClipFadeHandle aria-label="Groove fade out" edge="out" />
  </Timeline.Clip>
</Timeline.Lane>
```

All of it is in the timeline's units, the source's too. Dragging the clip's header, its title bar, moves it on the grid, and over other lanes: `eventDetails.lane` is the `Timeline.Lane` under the pointer, so put your own attribute on each lane (`data-track` above) and read it in `onValueCommitted`, which also fires for a drop on another lane that left the position as it was. The clip follows the pointer vertically through `--clip-drag-y`. A press on the clip's body selects it and puts the cursor there, and a drag scrubs, so the body never moves the clip by accident. Its content is yours: it sets `--clip-offset` and `--clip-source-length` so a `Waveform` can show the stretch that is played.

Holding Alt when a drag begins marks it as a copy (`eventDetails.copy`): the clip you were dragging is the copy, so leave the original where it was and add the dragged one. A fade's shape is stepped by double-clicking its handle, or Enter on its input (`reason` is `"double-click"` or `"keyboard"`), and `ClipFade` draws the real ramp. The registry's `TimelineClipFadeHandle` also takes `onShapeChange`: a right-click on the handle lists the four curves, each drawn, and the arrangement's `setFadeShape(id, edge, shape)` sets one as a single undo step.

Laying a clip where others already are is `coverClips(clips, placed, { duration }, split)`: the clips under it are covered rather than stacked (removed, trimmed or cut in two around it), and `split` makes the second piece of a cut clip from the first, so what your app keeps about a clip travels with each piece. Covering only shortens a clip, so the timeline's `duration` is the one limit it reads: the length of a source, which limits how far one clip stretches, never applies to the clips under it.

Cutting, copying and the like are plain functions on the clip, for the model behind your clips: `splitClip(clip, at)` returns the two pieces (or `null` when `at` leaves one too short), and `moveClip`, `trimClipStart`, `trimClipEnd`, `setClipFadeIn`, `setClipFadeOut`, `setClipFadeShape` and `duplicateClip` return the clip as it would end up, within the limits you give them.

#### Many clips

Dragging one clip among 1 000 takes about a millisecond of script, and the playhead moves outside React, so it costs no render. The real cost is rendering every clip again whenever one moves, which happens when the clip component is not memoized. Put one clip in a `React.memo` component with stable props (the clip's own `value`, callbacks that take its id), so React skips every clip a drag did not touch.

#### Selecting several clips

The primitive keeps no selection: `selected` and `onSelectedChange` are yours, so a selection is any set of ids you keep. `onSelectedChange` fires when a press selects a clip, and again on a press with Shift, Cmd or Ctrl held (read `eventDetails.event`), which is how you add to a selection or take from it. A touch screen has no modifier keys: give the clip `additive` (from a "select several" mode of your own) and every press is reported, so a press adds a clip to the selection or takes it from it. To move a group, read `eventDetails.delta` on a move, how far the dragged clip is from where the drag began, and move each other selected clip by the same amount from where it was when the drag began; `moveClip` keeps each on the timeline. For the group to follow the clip up and down too, `onDragOffsetChange` reports how far it is dragged vertically, in px, and 0 when the drag ends: shift the other selected clips by that much. A drop on another lane then moves each by as many lanes as the dragged one did. The registry's arrangement block does all of this, with Cmd/Ctrl + A to select every clip and Shift + drag across bare lane to select the clips a rectangle touches (it measures each clip's `getBoundingClientRect()`, so it needs nothing from the primitive).

#### The cursor

A `Timeline.Lane` with `seekable` puts the position where a press lands on its bare area, as the ruler does, and a drag scrubs it. Presses on the clips in it go to the clips. The cursor can then go anywhere along a track, and your track header selects the whole track. A split is then `splitClip(clip, position)` on the selected clip, or on each clip of the selected track that the cursor is over.

### Navigator

```tsx
<Timeline zoom={zoom} onZoomChange={setZoom} duration={32}>
  <TimelineViewport scrollbar="hidden">…</TimelineViewport>
  <TimelineNavigator />
</Timeline>
```

Place it anywhere inside the timeline, outside the viewport. Dragging an edge zooms while holding the other edge in place, through `onZoomChange` with `reason: "drag"`; a double-click fits the whole timeline with `reason: "double-click"`.

### Musical time

`@audio-ui/core/time` turns a tempo into what a timeline needs, in seconds:

- `gridInterval(division, zoom, tempo)` is the grid's step: a fixed division (`"1-bar"`, `"1/8"`, …), or `"adaptive"` for the finest division still 24px apart at this zoom. Pass `{ triplet: true }` for three in the space of two. Give the same number to `snapStep` and to the lanes' `gridStep`, so gestures land on the lines you see.
- `barLabelInterval(zoom, tempo)` is how far apart the ruler's bar numbers go: whole bars, spread out until they no longer collide.
- `barsBeatsAt(seconds, tempo)` and `describeBarsBeats` read a position as bars, beats and sixteenths.
- `gridLabel(seconds, tempo)` names an interval the way a grid readout does (`"1 bar"`, `"1/16"`, `"1/8T"`), so the adaptive grid reads out the division it uses, and a menu and a readout always name a grid the same way.
- `metronomeRhythmSeconds(rhythm, tempo)` is the interval of a click rhythm (`"1/8"`, `"1/8t"`, …), or `null` for `"auto"`, which follows the beat.

### Zoom and grid

Two hooks hold what the view needs to zoom and snap, so every arrangement zooms and snaps the same way:

```tsx
import { GRID_DIVISION_OPTIONS, useTimelineGrid } from "@/hooks/use-timeline-grid";
import { useTimelineZoom } from "@/hooks/use-timeline-zoom";

const viewportRef = useRef<HTMLDivElement>(null);
const view = useTimelineZoom({ duration, viewportRef });
const grid = useTimelineGrid({ tempo, zoom: view.zoom });

<Timeline duration={duration} snapStep={grid.snapStep} {...view.timeline}>
  <TimelineViewport ref={viewportRef}>
    <TimelineLane gridStep={grid.snapStep}>…</TimelineLane>
  </TimelineViewport>
</Timeline>
```

- `use-timeline-zoom` (`npx shadcn@latest add @audio-ui/use-timeline-zoom`) fits the whole arrangement in the room beside the track headers until it is zoomed, and never goes wider. A Cmd/Ctrl + scroll zooms around the pointer; `zoomAround(at, factor)` keeps a time where it is on screen, as the + and - keys do around the cursor, `zoomTo(start, end)` fits a stretch (Z on the selection), and `zoomBack()` steps back through the zooms they left (X). `ratio` and `setRatio` read and set it as a share of the fitted view, and `rescale(ratio)` keeps it on the same bars when the tempo changes.
- `use-timeline-grid` (`npx shadcn@latest add @audio-ui/use-timeline-grid`) is the grid: adaptive or a fixed division, triplet, snapping. `step` is what the lanes draw, `snapStep` what gestures snap to (0 with snapping off), and `label` names it for the button that opens `TimelineGridMenuItems` with `GRID_DIVISION_OPTIONS`. `change` is what Cmd/Ctrl + 1 to 5 do, by the key's place in `GRID_KEYS`: a finer or wider grid, triplets, snapping, and a grid fixed where it is or following the zoom again.

### Placing your own parts

Every lane sets a `--timeline-unit` CSS variable, the width of one unit. Parts are placed with it, so anything you draw follows the zoom the same way:

```tsx
<div style={{ left: "calc(var(--timeline-unit) * 12)", position: "absolute" }} />
```


## The arrangement

The parts on this page hold the arrangement and fill in what a DAW's arrangement needs around the timeline.

### The state

- `arrangement` (`npx shadcn@latest add @audio-ui/arrangement`) is the document: the types of a clip, a track and a mix, and what reads them. An audio clip names the recording it plays in `recording`, which every piece and copy of it keeps, and a track's `devices` and `sends` are its chain and what it sends to the return tracks. `clipReach` is how far a clip can stretch, `playedNotes` where a MIDI clip's notes play, and `mixAt` what a track plays at a point of the song. Its edits ship with `use-arrangement`, in `lib/arrangement-state`: `lay` puts a clip on a track so it covers what is under it, `land` drops a dragged group (or its copies) on another track, `shiftTrack` and `otherSelected` are what a group drop needs, and `scaleValue` rescales a clip when the tempo changes. Tracks are passed as an order of ids, so nothing assumes how many there are.
- `use-arrangement` is the state: tracks, clips, selection, each track's mix, its device chain and its sends, the return tracks and the main track, each track's height, the tempo, the time signatures, the locators and the loop, and every operation on them. It keeps undo and redo over all of them together.
- `use-simulated-clock` is a clock for a timeline that makes no sound, as a live source that loops between two points (to start sound at a position, `use-transport-clock` schedules against the audio clock), and `use-marquee-selection` is the rectangle that selects the clips it touches.

```tsx
const arrangement = useArrangement({
  duration, // the length of the song in seconds when it starts; a change of tempo scales it
  initialClips,
  initialLocators, // { at, name }[], in seconds
  initialLoop, // { on, range: [start, end] }
  initialTracks, // { id, name, color, kind?, mix?, devices?, sends? }[]
  initialReturns, // the return tracks, [] by default
  initialMain, // the main track, MAIN_TRACK by default
  tempo, // { bpm, beatsPerBar, beatUnit }
});

arrangement.clips; // what the arrangement holds, with tracks, tempo, signatures, locators, loop and duration
arrangement.dispatch({ type: "add-track" }); // every change is a command; it returns the state after it
arrangement.dispatch({ type: "undo" });
```

The state is a plain value and a change is a command of `ArrangementCommand`, which `lib/arrangement-state`'s `apply(state, command)` takes: a pure function, so the arrangement runs, and is tested, with no React. The hook holds the state and returns it with `dispatch`, which returns the state after the command. A command that changes nothing returns the same state and adds no step to the history.

- **Clips**: `add-clip`, `change-clip` (what `Timeline.Clip` reports on a drag, by `id`), `commit-clip` (lands the drag, on another track too), `edit-clip`, `remove-clip`, `rename-clip`, `set-fade-shape` (a fade's curve, as one undo step), `crop-clip`, `clip-action` (a clip's menu: duplicate, split, switch off, delete), `duplicate-selection` and `split-cursor` (`at` and the shortest piece `step` come with the command, since the host knows the playhead). A drag is a burst that `settle-clip-edit` closes into one step.
- **Selection**: `select-clip` (`id`, `additive`), `select-clips`, `select-all`, `select-track` (`id`, `additive`), `clear-selection` and `toggle-selected`. The hook returns `selectedIds`, `selectedTracks` and `selection`.
- **Tracks**: `add-track` (with a `name`, as a dropped file gives one, and an `id` it takes while no track has it, so a `batch` can put clips on the new track; `kind: "return"` adds a return track, named by its letter, after the others), `remove-track` (a return takes every send to it with it; the main track stays), `rename-track`, `duplicate-track` (the track with its clips and its mix, placed after it), `move-track`, `set-mix` (a switch: mute, solo) and `change-mix` (a drag of the gain or the pan, closed by `settle-clip-edit`), and `set-track-color`. A track's mix is its `mix`, so it is undoable; `mixOf(track)` is what a header reads. `heights`, `resizeTrack`, `folded` and `toggleFold` stay in the hook, since they are the view and not the document. `copyOf(time)` is a read, not a command: what a copy would hold.
- **Devices**: a track's chain is its `devices`, from first to last, each `{ id, device, state, on?, instrument? }`: `device` is the device's stable id, such as `audioui.compressor`, and `id` the place it holds, which stays the same as it moves. `add-device` puts one at `index` or at the end, `move-device` moves it along the chain or to another track's with `to`, `remove-device` and `toggle-device` (switched off, it passes the sound on unchanged) are one step each, `set-device` sets its state as one step and `change-device` is a turn of a knob, closed by `settle-clip-edit`. An instrument (`instrument: true`) goes first on a MIDI track, in place of the one there was, and no effect goes before it; it goes on no other track. The commands take the id of any track: one of the tracks, a return or the main track.
- **Sends and returns**: `set-send` and `change-send` (a drag, closed by `settle-clip-edit`) set how much a track sends to a return, `{ track, to, level }`, a gain from 0 (silence, the default) to 1 (0 dB). `set-send-mode` sets whether a return takes what it is sent before the senders' volume, pan and switch (`pre`) or after. The return tracks are `returns` and the main track `main`, which the mix commands (`set-mix`, `change-mix`, `rename-track`, `set-track-color`) reach by their ids like any track; `allTracks(doc)` and `trackOf(doc, id)` read them all.
- **Time**: `clear-time`, `paste`, `remove-time`, `insert-time`, `consolidate-time` and `fade-time` act on a time selection (`{ range, tracks }`), and `copyOf(time)` copies one; `lib/arrangement-state` holds them as pure functions: `copyTime`, `clearTime`, `pasteTime`, `deleteTime`, `openTime`, `consolidate`, with `crossfade` for a fade where two clips meet, given to both.
- **Tempo, signatures, locators and the loop**: `set-tempo` keeps the musical positions, so every time in the document is scaled to the new bar and a MIDI clip three bars long stays three bars long, as one undo step, since each step of the history carries its own tempo. A recording follows the tempo only when it is warped, through its markers, the recording itself staying as it is; one that is not warped keeps its speed, so its start follows the bar and it lasts as many seconds as before, and where that runs it into the next clip it ends there (`settleOverlaps`). Then `add-signature`, `set-signature`, `move-signature` and `remove-signature`, `add-locator`, `rename-locator` and `remove-locator`, `set-loop` (`{ on, range }`, either one) and `change-loop`, a drag of the range that is one step once `settle-clip-edit` closes it, and `set-duration`, which changes the song's length without a step. The hook returns `tempo`, `signatures`, `locators`, `loop` and `duration`.
- **History**: `undo` and `redo`, and `canUndo` and `canRedo`. `batch` (`{ commands }`) applies several commands in order as one step, each seeing what the one before it did: what one gesture that makes several changes sends, such as files dropped to make a track each.

### Arranged clips

`ArrangedClip` (`npx shadcn@latest add @audio-ui/arranged-clip`) is a clip of the arrangement on its track's lane: drag its title bar to move it (with Alt, a copy), its edges to trim it into its source, its top corners to fade it, and pick a fade's curve from the menu of its handle. Held near an edge of the view, a moved or trimmed clip scrolls it, so it can go past what is on screen or to a track below; let go off the tracks (over the ruler, or the rows under the last track), a moved clip goes to the nearest track that takes it, a recording to an audio track and a MIDI clip to a MIDI one. Double-click its title to rename it in place; right-click it for its menu (edit, rename, duplicate, split at the cursor, switch off, delete), which selects it first. A MIDI clip draws its notes across its body and an audio clip its waveform, from the `peaks` of its source, and a looped clip has no end while a warped one lasts as long as its warp stretches its source: `clipReach(clip)` in `lib/arrangement` gives how far a clip can be stretched, `undefined` when it loops.

```tsx
import { ArrangedClip, ArrangedClipGhost } from "@/components/audio-ui/arranged-clip";

<TimelineLane>
  {clips
    .filter((clip) => clip.track === track.id)
    .map((clip) => (
      <ArrangedClip
        clip={clip}
        describe={describe}
        key={clip.id}
        onAction={(action, id) =>
          arrangement.dispatch({
            type: "clip-action",
            action,
            id,
            at: clock.getSnapshot(),
            step: bar / 4,
          })
        }
        onChange={(id, value, details) =>
          arrangement.dispatch({ type: "change-clip", id, value, details })
        }
        onCommit={(id, details) =>
          arrangement.dispatch({
            type: "commit-clip",
            id,
            copy: Boolean(details.copy),
            track: details.lane instanceof HTMLElement ? details.lane.dataset.track : undefined,
          })
        }
        onFadeShape={(id, edge, shape) =>
          arrangement.dispatch({ type: "set-fade-shape", id, edge, shape })
        }
        onRemove={(id) => arrangement.dispatch({ type: "remove-clip", id })}
        onRename={(id, name) =>
          arrangement.dispatch({ type: "rename-clip", id, name })
        }
        onSelect={(id, additive) =>
          arrangement.dispatch({ type: "select-clip", id, additive })
        }
        selected={arrangement.selectedIds.includes(clip.id)}
        swatch={arrangement.mixOf(track).color}
        trackName={track.name}
      />
    ))}
</TimelineLane>;
```

While a clip is dragged with Alt, `arrangement.copying` lists the originals: draw an `ArrangedClipGhost` for each, where it stays. `lift` and `onLift` move the rest of a selection up or down with the clip being dragged across tracks.

With snapping on, a dragged clip or loop also lands on the edges of the other clips, the locators and the loop, through `snapPoints`. Where two clips meet, the fade-out of one and the fade-in of the next move together, as a crossfade, and a dashed line shows how far a fade being dragged can go.

`useArrangedClips` ships with them and wires a clip to the arrangement's commands. Spread `clipProps(clip)` on `ArrangedClip`, with what draws it (`describe`, `peaks`, `swatch`, `trackName`), and draw `ghostsOn(trackId)` with `ArrangedClipGhost` for the originals of a copy dragged with Alt. A clip dropped on a track moves to it if the track takes what it holds, and let go off the tracks it goes to the nearest one; the rest of a selection follows a clip dragged up or down.

### Arranged tracks

A track's header is `TimelineTrackControls`, from the timeline: its name, gain, pan, mute, solo, color, fold and menu. `useTrackReorder` (`npx shadcn@latest add @audio-ui/use-track-reorder`) moves tracks: give each track a grip, and drag the grip, or press Up or Down on it.

```tsx
import {
  TrackGrip,
  TrackReorderGhost,
  trackReorderClass,
} from "@/components/audio-ui/track-grip";
import { useTrackReorder } from "@/hooks/use-track-reorder";

const reorder = useTrackReorder({
  onMove: (id, index) => dispatch({ type: "move-track", id, index }),
  order: tracks.map((track) => track.id),
});

<TimelineTrack className={trackReorderClass(reorder, track.id)}>
  <TimelineTrackHeader>
    <TrackGrip
      dragging={reorder.draggingId === track.id}
      trackName={track.name}
      {...reorder.gripProps(track.id)}
    />
    …
  </TimelineTrackHeader>
</TimelineTrack>;

<TrackReorderGhost
  ghost={reorder.ghost}
  ghostRef={reorder.ghostRef}
  trackName={dragged?.name}
/>;
```

The grip sits in its track's row, inside the `Timeline`. While a track is dragged, `draggingId` is the track and `drop` (`{ id, edge }`) is the edge of the row it would land on: `trackReorderClass` fades the track where it was and draws a line at that edge. `ghost` is the box of its header, and `TrackReorderGhost` draws the copy under the pointer, fixed, which the drag moves without a render. The view scrolls when the pointer nears its top or bottom edge. The grip, the copy and the line ship with the hook, in `components/audio-ui/track-grip`.

### Groups

A track can sit in a group track: it names it in `group`, and a group's tracks follow it in `tracks`. The group is a track of its own (`kind: "group"`), with its chain, its mix and its sends, and the tracks in it play into it instead of into the main track. A group can hold groups.

- `{ type: "group-tracks", ids }` puts tracks into a new group where the first of them was. `{ type: "ungroup", id }` dissolves one, its tracks going up a level.
- A group moves, is duplicated and is removed with its tracks; a track moved among a group's tracks joins it.
- `TimelineTrackControls` puts "Group" and "Ungroup" in a track's menu when given `onGroup` and `onUngroup` (give `onUngroup` to a group track only).
- `unfoldedTracks` (from `useArrangement`) leaves out the tracks of a folded group. `trackBlock` and `trackDepth` give a group's tracks and how deep a track sits, to indent its header.
- `ArrangedGroupPreview`, from `arranged-clip`, draws on a group's lane what its tracks play.

### Time selections

`useTimeSelection` (`npx shadcn@latest add @audio-ui/use-time-selection`) is a stretch of time on some tracks and what the keyboard does with it. Tell it where a press on bare lane began, give its `onSelectionChange` to the lanes, and draw the overlay on the tracks it spans:

```tsx
import { TimelineTimeSelection } from "@/components/audio-ui/time-selection";
import { useTimeSelection } from "@/hooks/use-time-selection";

const selection = useTimeSelection({
  bar,
  clips,
  copyOf,
  dispatch,
  getPosition: () => engine.position.getSnapshot(),
  selectedIds,
  selectedTracks,
  tracks,
});

<TimelineLane
  data-track={track.id}
  onPointerDown={() => selection.pressLane(track.id)}
  onSelectionChange={selection.onSelectionChange}
>
  {selection.time?.tracks.includes(track.id) ? (
    <TimelineTimeSelection range={selection.time.range} />
  ) : null}
</TimelineLane>;
```

`handleKey` takes a key press, native or React's, and says whether it was one of its own, so a host's other keys can follow: Cmd/Ctrl + C and X copy and cut (the selected clips when no time is selected), V pastes at the cursor and I opens silence there; with a time selection, D duplicates it after itself, J makes one clip per track of it, L loops it and Delete empties it. With Shift too, X, V, D and Delete work on the time itself, on every track, and Cmd/Ctrl + Alt + F makes fades from it. Each is one step of undo.

### Locators and time signatures

`TimelineLocator` and `TimelineSignature` (`npx shadcn@latest add @audio-ui/timeline-markers`) go on a lane of their own, and stand on their time with their line. A locator is a named point: press it to put the cursor there, double-click it to play from there, drag it or press Left and Right to move it on the grid (Cmd/Ctrl lets go of the grid), press Enter or pick Rename in its menu to rename it, Delete to take it away. A change of time signature moves the same way. The block's M adds one at the cursor, and Alt with the arrows jumps from one to the next.

```tsx
import { TimelineLocator, TimelineSignature } from "@/components/audio-ui/timeline-markers";

<TimelineLane>
  {locators.map((locator) => (
    <TimelineLocator
      key={locator.id}
      locator={locator}
      onJump={seek}
      onMove={moveLocator}
      onPlay={playFrom}
      onRemove={removeLocator}
      onRename={renameLocator}
      step={grid}
    />
  ))}
</TimelineLane>;
```

`useTimelineMarkers` ships with them and holds the commands: spread its `locator` on `TimelineLocator` and its `signature` on `TimelineSignature`, with `editing` set for the change `editingSignature` names. `addLocator` puts a locator at the cursor, `addSignature` a change, opened to be typed. `TimelineBarMarks`, inside a `TimelineRuler`, numbers the bars through those changes, every few bars at a far zoom, and hatches the ones a change cuts short.

`useTimelineCursor` (`npx shadcn@latest add @audio-ui/use-timeline-cursor`) is what the keys do with the cursor. `handleKey` takes M, which adds a locator, and Alt with the arrows, which jump to the next locator or clip edge, of the picked tracks or of every track. `handleArrowKey` takes the plain arrows, Home and End, for when a key reached the view rather than a control with arrows of its own: Left and Right move the cursor on the grid, Up and Down pick a track. Both say whether the key was theirs.

The Signature strip holds time-signature changes: its + adds one at the cursor, in the signature already there, ready to be typed (such as 7/8). From a change on, bars are counted in its signature: the ruler's numbers, the hatched partial bar a change cuts short, and the position in the transport all follow, through `barsOf` and `barsBeatsWith` from `@audio-ui/core/time`. On a change, Left and Right move it by a grid step, Enter edits it and Delete takes it away. The grid's bar-long divisions and the tempo stay those of the song's start. Which change is being typed is yours to hold (`editing` and `onEditingChange`), so a change just added opens ready to type. `parseSignature("7/8")` reads what is typed.

### Audio files

Drag an audio file from the desktop onto an audio track: the track is outlined while the file is over it, and let go, the file becomes a clip at the grid line under the pointer (anywhere with Cmd/Ctrl), named and as long as the file. Several files follow each other on the track, and a file let go anywhere else in the view does nothing. The block decodes a file at the engine's rate with `decodeAudioChannels` from `lib/audio-decode` (`npx shadcn@latest add @audio-ui/audio-decode`), keeps it as a recording the clip names (`recording`), and takes its peaks with `computePeaks` from `@audio-ui/core/analysis`: the clip draws its waveform, and it plays.

### Automation

Press **A**, or the automation button, for automation mode: each track's header gets a chooser of what its lane shows, Volume or Pan, and its lane the envelope of that parameter over its clips (`automation-envelope`, `npx shadcn@latest add @audio-ui/automation-envelope`). Until the track has one, the line stands faint at the mix's value; double-click it to add a point, drag a point to move it on the grid (its level stays free), drag the small handle on a segment to bend it, and double-click a point to take it away. Beside the line, a press reaches the lane and the clips as before. Each track's channel follows its envelopes as it plays. Moving an automated gain or pan by hand overrides it: the envelope greys, the mix plays, and **Re-enable automation** in the toolbar (or drawing on the envelope) brings it back. In `lib/arrangement`, a track keeps its `automation` per parameter as points across the whole arrangement, `x` from 0 to 1, so a tempo change keeps them in place; `automationAt` reads one, `volumeGain` and `panPosition` turn its values into a gain and a pan, and `mixAt` gives what a track plays at a point of the song.

`TrackAutomationChooser` and `TrackAutomation` (`npx shadcn@latest add @audio-ui/track-automation`) are those two parts: the chooser goes in the track's header (`TimelineTrackControls` takes it as children), the envelope in its lane. `AUTOMATED_PARAMETERS` lists what a track automates, Volume and Pan. `useTrackAutomation` ships with them and holds the mode:

```tsx
import { useTrackAutomation } from "@/hooks/use-track-automation";

const automation = useTrackAutomation({ dispatch, tracks });
const engine = useArrangementEngine({ doc, overridden: automation.overridden, recordings });

<TimelineTrackControls onMix={automation.changeMix} …>
  {automation.automating ? (
    <TrackAutomationChooser
      onClear={automation.clear}
      onParameterChange={automation.show}
      parameter={automation.parameterOf(track.id)}
      track={track}
    />
  ) : null}
</TimelineTrackControls>;
```

`automating` and `toggle` are the mode, `parameterOf` and `show` what each lane shows. A gain or a pan moved through `changeMix` overrides an automated one: `overridden` lists, by track, what plays its mix instead, for the engine and for `TrackAutomation`'s greyed line, until `reenable` (`anyOverridden` says when to offer it) or until `edit` draws that envelope again. `clear` takes an envelope away in one step of undo.

### MIDI clips

A track can also hold MIDI: Bass and Keys are MIDI tracks, and **+ MIDI** under the tracks adds one. A MIDI clip draws its notes across its body and, picked, opens on a piano roll in the clip panel (`midi-clip-panel`, `npx shadcn@latest add @audio-ui/midi-clip-panel`), with the same strip and Clip and Loop boxes (Loop adds Dupl. Loop, which plays the loop's notes again after it) and a Notes box (transpose, invert, reverse, legato, ×2, ÷2, humanize and velocity ramps, on the selected notes or every note): draw notes a grid step at a time, drag the loop brace on its ruler, move, stretch and edit them as in the [piano roll](/docs/components/base/piano-roll), and each edit is one undo step of the arrangement. Double-click a bare MIDI lane for an empty clip a bar long, which loops as it is stretched. While the arrangement plays, their notes go to the instrument of the track's chain (in the block, a pocket synth on Bass and another on Keys), each at its frame of the audio clock, through the track's channel; a key pressed in the piano roll plays the same instrument. In `lib/arrangement`, a track's `kind` is `"audio"` or `"midi"`, a MIDI clip keeps its `notes` in its source's time, and `playedNotes` gives where they play through the clip's offset and loop.

The block plays the whole arrangement through the [arrangement engine](/docs/engine/playback) (`npx shadcn@latest add @audio-ui/arrangement-engine`): its audio clips, its notes, each track's chain and mix, its automation. `useMidiPlayback` (`npx shadcn@latest add @audio-ui/use-midi-playback`) is a lighter way to hear MIDI clips alone, with no recordings and no devices: give it the clips, the tracks, the arrangement's clock and whether it plays; `audible` says whether a track is heard and `levelsAt` gives a track's gain and pan at a time, from its mix or its automation. It returns `onNoteOn` and `onNoteOff` for a MIDI clip's editor, which sound what it auditions. Its voices and its bus are the Audio engine's [Synthesis](/docs/engine/synthesis) and [Output](/docs/engine/output); with an engine of your own, leave both out and play the clips from `playedNotes`.

### The clip panel

Under the tracks, the clip panel (`clip-panel`, `npx shadcn@latest add @audio-ui/clip-panel`) shows the clip you pick, as a detail view follows the selection. It starts closed, and a press that picks a clip, trims it, fades it or moves it does not open it; its × hides it. It opens on a click of a clip's head, a double-click on a clip's body, the clip menu's Edit, or `Cmd` / `Ctrl` + `Alt` + `3`, but not on a press that moves, trims or fades a clip, so you can work on a clip with the panel closed. Laid out as a DAW's clip view (`clip-view` holds the frame), a strip along its top carries the caret that rolls the panel up to that strip, the clip's switch, its name (type to rename it) and a word on it; under it, its properties sit in boxes beside the editor: Clip (where it starts and ends in its source), Loop (the switch, where the loop sits and how long it is, ÷2 and ×2) and Sample (Warp, Reverse, Crop, Gain and Transpose), all in bars.beats.16ths you can type or step with the arrow keys. Loop and Sample fold to a strip from their heading and give their width to the editor; what is rolled up or folded stays so from clip to clip, and the panel stays at the bottom of the block as the tracks scroll. Given a height of its own, a dock under the tracks, the panel fills it: the boxes reach its bottom, and the waveform, or a MIDI clip's roll, takes the room there is. Its source has a timeline of its own: an overview bar to zoom and scroll with, a ruler in bars, the stretch the clip plays to drag at both ends, or with Loop on the stretch that repeats (a looped clip can then be stretched past its source on the timeline), and the waveform under them. With Warp on, the source is stretched between markers that pin a time in it to a time on the clip: drag a marker, or press its arrows, to stretch what is on either side; double-click the waveform to add one, Delete to take one away, and ×2 or ÷2 to play the whole of it slower or faster. It plays by speed alone, so pitch follows (shown as a rate and its semitones), and a warped clip lasts as long as its warp stretches its source on the timeline. Reverse plays the same stretch of the source backwards: the region and the loop are mirrored with it (`reversedSettings`), so a clip that played the first bar of a recording plays that bar backwards, not the last one. The map is `warpedTime`, `unwarpedTime`, `warpRate` and the marker functions in `@audio-ui/core/time`. These are kept on the clip (`gain`, `transpose`, `reversed`, `loop`, `warp`, `sourceFrom` in `lib/arrangement`), and the engine plays them as they are set.

### Keyboard

A drag across the bare lanes selects a stretch of time on the tracks it sweeps, drawn over them; the time commands below work on it. The block's own keys work once a press on a lane has put the focus in it:

<KeyTable title="Keys">
<KeyRow gesture={"`Left` / `Right`"}>Move the cursor by a grid step. `Home` and `End` go to the start and to the end of the last clip.</KeyRow>
<KeyRow gesture={"`Alt` + `Left` / `Right`"}>Jump to the previous or next locator or clip edge, on the picked tracks (on every track when none is picked).</KeyRow>
<KeyRow gesture={"`Up` / `Down`"}>Pick the track above or below.</KeyRow>
<KeyRow gesture={"`+` / `-`"}>Zoom in or out around the cursor.</KeyRow>
<KeyRow gesture={"`Z`, `X`"}>Zoom onto the selected clips (the loop, when none is selected), and step back to the zoom before.</KeyRow>
<KeyRow gesture={"`Cmd` / `Ctrl` + `1`, `2`"}>A finer or a wider grid.</KeyRow>
<KeyRow gesture={"`Cmd` / `Ctrl` + `3`, `4`, `5`"}>Triplets on or off, snapping on or off, and a grid fixed where it is or following the zoom again.</KeyRow>
<KeyRow gesture={"`M`"}>Add a locator at the cursor.</KeyRow>
<KeyRow gesture={"`S`, `Cmd` / `Ctrl` + `E`"}>Split the selected clips at the cursor.</KeyRow>
<KeyRow gesture={"`Cmd` / `Ctrl` + `L`"}>Put the loop around the time selection, or around the selected clips.</KeyRow>
<KeyRow gesture={"`Cmd` / `Ctrl` + `C`, `X`, `V`"}>Copy or cut the time selection (the selected clips when there is none), and paste it at the cursor, from the picked track down.</KeyRow>
<KeyRow gesture={"`Delete`"}>Empty the time selection on its tracks.</KeyRow>
<KeyRow gesture={"`Cmd` / `Ctrl` + `D`, `J`"}>With a time selection: repeat it right after itself, and make one clip per track of it.</KeyRow>
<KeyRow gesture={"`Cmd` / `Ctrl` + `Shift` + `X`, `V`, `D`, `Delete`"}>The same on time itself, on every track: cut it out, paste it in, repeat it, or take it out, moving what comes after.</KeyRow>
<KeyRow gesture={"`Cmd` / `Ctrl` + `I`"}>Open a stretch of silence at the cursor, as long as the time selection (a bar when there is none).</KeyRow>
<KeyRow gesture={"`0`"}>Switch the selected clips off, or back on.</KeyRow>
<KeyRow gesture={"`Cmd` / `Ctrl` + `Alt` + `F`"}>Fade the clips whose edges the time selection covers, in or out; two clips that meet across it crossfade.</KeyRow>
<KeyRow gesture={"`Delete` on a fade handle"}>Take the fade away.</KeyRow>
<KeyRow gesture={"`Cmd` / `Ctrl` + `Alt` + `3`"}>Show or hide the clip panel.</KeyRow>
<KeyRow gesture={"On the loop"}>`Left` / `Right` move it by a grid step, `Up` / `Down` by its own length; with `Cmd` / `Ctrl`, `Left` / `Right` shorten or lengthen it and `Up` / `Down` double or halve it.</KeyRow>
</KeyTable>

### Other DAWs

The `dawproject` lib (`npx shadcn@latest add @audio-ui/dawproject`) writes an arrangement to a `.dawproject` file and reads one back. DAWproject is the open exchange format several DAWs read and write: a zip of `project.xml`, `metadata.xml` and the audio.

```ts
import { exportDawproject, importDawproject } from "@/lib/dawproject";

// Out: every recording a clip plays, by its id, with the file it goes in.
const { file, report } = await exportDawproject(doc, recordings, readBytes);

// In: the arrangement, and the bytes of each recording under the id its clips name.
const { doc: opened, recordings: files, report: lost } = await importDawproject(blob);
```

- **What crosses over:** the tracks, the return tracks and the main track, with their volume, pan, mute, solo and sends. Audio clips with their fades, loop and warp, MIDI clips with their notes. The automation of volume and pan. The locators, the tempo and the time signatures. The equalizer, compressor, gate and limiter, whose settings the format describes.
- **What is left out:** a reversed clip, a clip's gain, the loop and any other device. Each loss is a line of `report`.
- **Times:** written in seconds, and read in seconds or beats.

### Undo and redo

The `undo-history` lib (`npx shadcn@latest add @audio-ui/undo-history`) implements undo and redo as values: every function takes the present and returns the new present, and keeps no state of its own. Keep the history in a ref and the document in state, so two undos in one tick are fed the first one's result instead of reading the same stack twice.

```ts
let history = emptyHistory<Clip[]>();

// A change made in one go: delete, split, duplicate.
history = recordChange(history, clips);

// A drag reports every tick: open a burst on the first, close it on commit,
// and the whole drag is one entry.
history = beginChange(history, clips);
history = settleChange(history);

const step = undo(history, clips); // { history, present } or null
```

A new change clears redo, since the redo branch no longer follows from the present. `HISTORY_LIMIT` bounds the depth. The arrangement block uses it for its undo and redo buttons and for Cmd/Ctrl + Z, with Shift (or Y) to redo.


## API reference

### Root

Groups the parts of a timeline and owns what they share: its length, its zoom, its grid and the playback position. Units are yours: seconds, beats, pulses or steps. `zoom` is how many px one unit takes; leave it unset and the timeline fits the width it is given. The position comes from `position`, or from a live `source` that the playhead follows straight in the DOM without re-rendering React. With a `snapStep`, every gesture lands on the grid unless Cmd/Ctrl is held, and `snapPoints` (the edges of other clips, the locators) pull a dragged clip or range onto them from a few px away. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"defaultPosition"} type={"number"} default={"0"}>Uncontrolled starting position.</TypeRow>
<TypeRow name={"defaultZoom"} type={"number"}>Uncontrolled starting zoom, in px per unit. Unset, the timeline fits its width until zoomed.</TypeRow>
<TypeRow name={"disabled"} type={"boolean"}></TypeRow>
<TypeRow name={"duration"} type={"number"} default={"1"}>Length of the timeline, in your units.</TypeRow>
<TypeRow name={"largeStep"} type={"number"}>Step for Page Up/Page Down and Shift + arrow keys. Defaults to ten `step`s.</TypeRow>
<TypeRow name={"maxZoom"} type={"number"} default={"Infinity"}></TypeRow>
<TypeRow name={"minZoom"} type={"number"} default={"0"}></TypeRow>
<TypeRow name={"onPositionChange"} type={"(value: number, eventDetails: PositionChangeEventDetails) => void"}>Fires while the position is moved: press, drag, keyboard and input changes.</TypeRow>
<TypeRow name={"onPositionCommitted"} type={"(value: number, eventDetails: PositionCommitEventDetails) => void"}>Fires when a gesture that moved the position ends.</TypeRow>
<TypeRow name={"onZoomChange"} type={"(zoom: number, eventDetails: ZoomChangeEventDetails) => void"}>Fires when the zoom changes, already clamped. `eventDetails.cancel()` keeps the zoom where it was.</TypeRow>
<TypeRow name={"position"} type={"number"}>Playback position, from 0 to `duration`.</TypeRow>
<TypeRow name={"snapOffset"} type={"number"} default={"0"}>Where the grid's lines start, in your units.</TypeRow>
<TypeRow name={"snapPoints"} type={"readonly number[]"}>Places that pull a dragged clip or range onto them from a few px away, ahead of the grid: the edges of other clips, the locators. Cmd/Ctrl lets go of them too.</TypeRow>
<TypeRow name={"snapStep"} type={"number"} default={"0"}>Distance between grid lines, in your units. 0 is no grid.</TypeRow>
<TypeRow name={"source"} type={"Source"}>A live position followed outside React; takes precedence over `position`.</TypeRow>
<TypeRow name={"step"} type={"number"}>Step for the arrow keys. Defaults to `snapStep`, or a hundredth of `duration`.</TypeRow>
<TypeRow name={"zoom"} type={"number"}>Zoom, in px per unit.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-fitted"}>Whether no zoom is set, so the timeline fits the width it is given.</TypeRow>
<TypeRow name={"data-scrubbing"}>Whether a pointer is moving the position.</TypeRow>
</TypeTable>

### Navigator

The whole timeline as one bar, with the stretch the viewport shows drawn inside it as `Timeline.NavigatorWindow`: drag the window to scroll, drag one of its `Timeline.NavigatorHandle`s to zoom, double-click to show everything. Place it anywhere inside `Timeline.Root`, outside the viewport. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"getAriaValueText"} type={"DescribePosition"}>Returns how a position is announced by the window and its handles. Defaults to the number.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-fitted"}>Whether no zoom is set, so the timeline fits the width it is given.</TypeRow>
<TypeRow name={"data-scrubbing"}>Whether a pointer is moving the position.</TypeRow>
</TypeTable>

### NavigatorWindow

The stretch of the timeline the viewport shows. Dragging it scrolls; its native `<input type="range">` scrolls from the keyboard and carries its accessible name. Renders a `<div>` element.

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-fitted"}>Whether no zoom is set, so the timeline fits the width it is given.</TypeRow>
<TypeRow name={"data-scrubbing"}>Whether a pointer is moving the position.</TypeRow>
</TypeTable>

### NavigatorHandle

One edge of the window: dragging it zooms, holding the other edge in place. Its native `<input type="range">` does the same from the keyboard and carries its accessible name. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"edge"} type={"\"end\" | \"start\""}>The edge of the window this handle moves.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-fitted"}>Whether no zoom is set, so the timeline fits the width it is given.</TypeRow>
<TypeRow name={"data-scrubbing"}>Whether a pointer is moving the position.</TypeRow>
</TypeTable>

### Viewport

The scrolling window onto the timeline. Cmd/Ctrl + wheel (and pinch on a trackpad) zooms around the pointer; a bare wheel scrolls as usual. Every zoom change keeps its anchor point in place, or the middle of the view when there is none. A clip moved or trimmed with the pointer held near its edges scrolls it. It reports its scroll offset and width for `Timeline.Navigator`. Renders a `<div>` element.

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-fitted"}>Whether no zoom is set, so the timeline fits the width it is given.</TypeRow>
<TypeRow name={"data-scrubbing"}>Whether a pointer is moving the position.</TypeRow>
</TypeTable>

### Lane

A strip along the timeline that its parts are placed on: a track, the ruler's strip, a row of markers. Zoomed, it is as long as the timeline; fitted, it takes the width it is given and the timeline is spread across it. Its `--timeline-unit` CSS variable is the width of one unit. With `seekable`, a press on its bare area moves the position, as on the ruler, so the cursor can be put anywhere along a track; with `onSelectionChange` too, a drag from there selects a stretch of time instead of scrubbing. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"seekable"} type={"boolean"} default={"false"}>Whether a press on the lane's bare area moves the position, and a drag scrubs it, on the grid unless Cmd/Ctrl is held.</TypeRow>
<TypeRow name={"onSelectionChange"} type={"(range: readonly [number, number], eventDetails) => void"}>With `seekable`, fires while a drag from the lane's bare area selects a stretch of time, as `[start, end]` in timeline units on the grid unless Cmd/Ctrl is held: from where the press put the position to the pointer. The drag then no longer scrubs. `eventDetails.cancel()` keeps the selection as it was.</TypeRow>
<TypeRow name={"onSelectionCommitted"} type={"(range: readonly [number, number], eventDetails) => void"}>Fires when a selection that changed ends.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-fitted"}>Whether no zoom is set, so the timeline fits the width it is given.</TypeRow>
<TypeRow name={"data-scrubbing"}>Whether a pointer is moving the position.</TypeRow>
</TypeTable>

### Track

One track of the arrangement: a row holding a header beside a lane. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"height"} type={"number"}>The height in px, or leave it out for the row to share what room there is.</TypeRow>
<TypeRow name={"minHeight / maxHeight"} type={"number"} default={"32 / 480"}>Bounds the height is clamped to.</TypeRow>
<TypeRow name={"step / largeStep"} type={"number"} default={"8 / 32"}>How far the arrows, and Page Up and Page Down, move the height.</TypeRow>
<TypeRow name={"selected"} type={"boolean"} default={"false"}>Whether the track is the one picked: only styling, `data-selected`.</TypeRow>
<TypeRow name={"onHeightChange"} type={"(height | undefined, eventDetails) => void"}>A new height is asked for, by a drag or a key; a double-click on the handle gives the height up, which is `undefined`. `eventDetails.cancel()` refuses it.</TypeRow>
<TypeRow name={"onHeightCommitted"} type={"(height | undefined, eventDetails) => void"}>A resize ended having changed the height.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-selected"}>Present when the track is selected.</TypeRow>
<TypeRow name={"data-resizing"}>Whether a pointer is resizing the track.</TypeRow>
</TypeTable>

### TrackHeader

The strip beside a track's lane that names it and holds what sets it. It stays at the left edge while the lane scrolls under it, at the stacking level of the `--timeline-track-header-z` CSS variable (20): raise it while the header is hovered or focused if it shows something that reaches into the next row, such as a value readout. It carries the track's `data-selected` and `data-resizing`. Renders a `<div>` element.

### TrackResizeHandle

Sets the track's height: by dragging, or from the keyboard through the native `<input type="range">` it holds, which carries the accessible name. The arrows move by a step (Down is taller), Page Up and Page Down by a large one, Home and End to the minimum and the maximum. Place it along the track's lower edge. A double-click clears the height, so the track shares the room again. Renders a `<div>` element.

### Ruler

The lane that moves the position: a press jumps to the pointer and a drag scrubs, both on the grid unless Cmd/Ctrl is held; with Shift held the drag scrubs four times finer. With `onSelectionChange`, a drag that starts with Shift held selects a stretch instead, to loop. It holds the native `<input type="range">` that takes focus, handles the keyboard and carries the accessible name (`aria-label` / `aria-labelledby`). Place `Timeline.Mark`s in it for its labels. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"getAriaValueText"} type={"DescribePosition"}>Returns how the position is announced, e.g. `"bar 3, beat 2"`. Defaults to the number.</TypeRow>
<TypeRow name={"onSelectionChange"} type={"(range: readonly [number, number], eventDetails) => void"}>Fires while a stretch is selected by dragging with Shift held, as `[start, end]` in timeline units on the grid unless Cmd/Ctrl is held: feed it to a `Timeline.Range` as a loop. A plain press and drag still move the position. `eventDetails.cancel()` keeps the selection as it was.</TypeRow>
<TypeRow name={"onSelectionCommitted"} type={"(range: readonly [number, number], eventDetails) => void"}>Fires when a selection that changed ends.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-fitted"}>Whether no zoom is set, so the timeline fits the width it is given.</TypeRow>
<TypeRow name={"data-scrubbing"}>Whether a pointer is moving the position.</TypeRow>
</TypeTable>

### Mark

Something placed at `value` on a lane: a bar number on the ruler, a cue, a grid line. Its children are its label. With `onValueChange` or `onValueCommitted` it moves: a drag takes it along the lane, on the grid unless Cmd/Ctrl is held, landing on a `snapPoints` place within a few px, and Left and Right on something focused inside it move it by `step`. A press that stays one keeps its click; a drag does not click.

<TypeTable>
<TypeRow name={"value"} type={"number"}>Where the mark sits, in the timeline's units.</TypeRow>
<TypeRow name={"onValueChange"} type={"(value: number, eventDetails) => void"}>Fires as the mark moves, on each step of a drag and each arrow key. With it or `onValueCommitted`, the mark can be moved.</TypeRow>
<TypeRow name={"onValueCommitted"} type={"(value: number, eventDetails) => void"}>Fires once a drag or an arrow key has moved it, with where it landed.</TypeRow>
<TypeRow name={"step"} type={"number"}>How far Left and Right move it. Defaults to the timeline's `step`.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-dragging"}>Whether a pointer is moving the mark.</TypeRow>
</TypeTable>

### Range

A stretch of the timeline, such as a loop, a selection or a clip, laid on the lane it sits in from `value[0]` to `value[1]`. Dragging its body moves it, on the grid unless Cmd/Ctrl is held; add a `Timeline.RangeHandle` per edge to resize it. With `drawable`, dragging across empty lane draws a new range. Its own native `<input type="range">` moves it from the keyboard and carries its accessible name (`aria-label` / `aria-labelledby`): Left and Right by a step, Up and Down by its own length, and with Cmd/Ctrl, Left and Right shorten or lengthen it by a step while Up and Down double or halve it. A drag lands an edge on a `snapPoints` place within a few px. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"defaultValue"} type={"Value"}>Uncontrolled starting range. Defaults to the whole timeline.</TypeRow>
<TypeRow name={"disabled"} type={"boolean"}></TypeRow>
<TypeRow name={"drawable"} type={"boolean"} default={"false"}>Whether dragging across empty lane draws a new range.</TypeRow>
<TypeRow name={"getAriaValueText"} type={"DescribePosition"}>Returns how a position is announced, for the range and its handles. Defaults to the number.</TypeRow>
<TypeRow name={"minLength"} type={"number"}>The shortest the range can be, in the timeline's units. Defaults to the grid step.</TypeRow>
<TypeRow name={"movable"} type={"boolean"} default={"true"}>Whether dragging the body, or its keys, moves the range.</TypeRow>
<TypeRow name={"onValueChange"} type={"(value: Value, eventDetails: ChangeEventDetails) => void"}>Fires while the range is moved, resized or drawn.</TypeRow>
<TypeRow name={"onValueCommitted"} type={"(value: Value, eventDetails: CommitEventDetails) => void"}>Fires when a gesture that changed the range ends.</TypeRow>
<TypeRow name={"value"} type={"Value"}>The range, as `[start, end]` in the timeline's units.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-dragging"}>Whether a pointer is moving, resizing or drawing the range.</TypeRow>
</TypeTable>

### RangeHandle

Resizes one edge of the range: by dragging, on the grid unless Cmd/Ctrl is held, or from the keyboard through the native `<input type="range">` it holds, which carries the accessible name. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"edge"} type={"\"end\" | \"start\""}>The edge of the range this handle resizes.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-dragging"}>Whether a pointer is moving, resizing or drawing the range.</TypeRow>
</TypeTable>

### Clip

A stretch of a source laid on a lane. Dragging its `Timeline.ClipHeader` moves it, on the grid unless Cmd/Ctrl is held, and over other lanes too, or to the nearest when the pointer is off them; held near an edge of the view, a move or a trim scrolls it, and a move scrolls down to the lanes below too; pressing its body selects it and puts the cursor there. Add a `Timeline.ClipHandle` per edge to trim it and a `Timeline.ClipFadeHandle` per fade. It sets `--clip-offset`, `--clip-source-length`, `--clip-fade-in` and `--clip-fade-out` for its content, and `--clip-fade-in-max` and `--clip-fade-out-max`, how long each fade can grow, to draw the limit of a fade being dragged (`data-fading`). Its own native `<input type="range">` moves it from the keyboard and carries its accessible name (`aria-label` / `aria-labelledby`); Delete and Backspace on it call `onRemove`. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"value"} type={"Value"}>The clip: `{ start, length, offset, fadeIn, fadeOut }`, all in the timeline's units.</TypeRow>
<TypeRow name={"defaultValue"} type={"Value"}>Uncontrolled starting clip. Defaults to the whole timeline.</TypeRow>
<TypeRow name={"sourceLength"} type={"number"}>How long the source is, in the timeline's units: trimming stops where it runs out. Defaults to no limit.</TypeRow>
<TypeRow name={"selected"} type={"boolean"}>Whether the clip is selected.</TypeRow>
<TypeRow name={"defaultSelected"} type={"boolean"} default={"false"}>Whether the clip starts selected, when `selected` is not set.</TypeRow>
<TypeRow name={"additive"} type={"boolean"} default={"false"}>Whether every press is reported, as one with Shift, Cmd or Ctrl held is: for building a selection where there are no modifier keys, such as a touch screen.</TypeRow>
<TypeRow name={"onSelectedChange"} type={"(selected: boolean, eventDetails) => void"}>Fires when a press on the clip selects it, and again on a press with Shift, Cmd or Ctrl held (or any, when `additive`), which `eventDetails.event` carries.</TypeRow>
<TypeRow name={"onValueChange"} type={"(value: Value, eventDetails: ChangeEventDetails) => void"}>Fires while the clip is moved, trimmed or faded.</TypeRow>
<TypeRow name={"onValueCommitted"} type={"(value: Value, eventDetails: CommitEventDetails) => void"}>Fires when a gesture that changed the clip ends, or one that dropped it on another lane.</TypeRow>
<TypeRow name={"onRemove"} type={"(eventDetails) => void"}>Fires when Delete or Backspace is pressed on the clip: removing it is up to you.</TypeRow>
<TypeRow name={"minLength"} type={"number"}>The shortest the clip can be trimmed to, in the timeline's units. Defaults to the grid step.</TypeRow>
<TypeRow name={"movable"} type={"boolean"} default={"true"}>Whether dragging the header, or the keys, moves the clip.</TypeRow>
<TypeRow name={"getAriaValueText"} type={"DescribePosition"}>Returns how a position is announced. Defaults to the number.</TypeRow>
<TypeRow name={"disabled"} type={"boolean"}></TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-dragging"}>Whether a pointer is moving, trimming or fading the clip.</TypeRow>
<TypeRow name={"data-fading"}>`"in"` or `"out"`: the fade a pointer is dragging.</TypeRow>
<TypeRow name={"data-selected"}>Present when the clip is selected.</TypeRow>
</TypeTable>

### ClipHeader

The clip's title bar: dragging it moves the clip, on the grid unless Cmd/Ctrl is held, and over other lanes too. The rest of the clip is its body, where a press puts the cursor. Place it along the clip's top and put the clip's name in it. Renders a `<div>` element.

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-dragging"}>Whether a pointer is moving, trimming or fading the clip.</TypeRow>
<TypeRow name={"data-selected"}>Present when the clip is selected.</TypeRow>
</TypeTable>

### ClipHandle

Trims one edge of the clip: by dragging, on the grid unless Cmd/Ctrl is held, or from the keyboard through the native `<input type="range">` it holds, which carries the accessible name. Trimming the start moves where in the source the clip begins and leaves its content where it is; trimming the end stops where the source or the timeline does. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"edge"} type={"\"end\" | \"start\""}>The edge of the clip this handle trims.</TypeRow>
</TypeTable>

### ClipFadeHandle

Sets how long one of the clip's fades lasts: by dragging, or from the keyboard through the native `<input type="range">` it holds, which carries the accessible name. Double-clicking it, or pressing Enter on its input, steps the fade through its shapes, and Delete or Backspace takes the fade away. It sits where its fade ends, at the clip's top edge. The two fades cannot overlap. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"edge"} type={"\"in\" | \"out\""}>The fade of the clip this handle sets: the one at its start or at its end. Double-click it, or press Enter on its input, to step the fade through its shapes.</TypeRow>
</TypeTable>

### ClipFade

Draws a fade over the clip's content, as a DAW does: the ramp as a line from the clip's edge, as long as the fade, with what it attenuates lightly shaded. It is hidden while its fade is empty and ignores the pointer. Its color is the `color` it is given. Renders an `<svg>` element.

<TypeTable>
<TypeRow name={"edge"} type={"\"in\" | \"out\""}>The fade of the clip this draws.</TypeRow>
</TypeTable>

### Playhead

A marker at the playback position, on the lane it sits in. It follows the position straight in the DOM, through its own `--timeline-offset` CSS variable, so a playhead moving every frame never re-renders React. Renders a `<div>` element.

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-fitted"}>Whether no zoom is set, so the timeline fits the width it is given.</TypeRow>
<TypeRow name={"data-scrubbing"}>Whether a pointer is moving the position.</TypeRow>
</TypeTable>

### Mark

Something placed at `value` on a lane: a bar number on the ruler, a cue, a grid line. Its children are its label. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"value"} type={"number"}>Where the mark sits, in the timeline's units.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-fitted"}>Whether no zoom is set, so the timeline fits the width it is given.</TypeRow>
<TypeRow name={"data-scrubbing"}>Whether a pointer is moving the position.</TypeRow>
</TypeTable>

### Registry component

#### Timeline

<TypeTable>
<TypeRow name={"duration"} type={"number"} default={"1"}>Length of the timeline, in your units.</TypeRow>
<TypeRow name={"zoom"} type={"number"}>Zoom, in px per unit.</TypeRow>
<TypeRow name={"defaultZoom"} type={"number"}>Uncontrolled starting zoom, in px per unit. Unset, the timeline fits its width until zoomed.</TypeRow>
<TypeRow name={"minZoom / maxZoom"} type={"number"} default={"0 / Infinity"}>Bounds the zoom is clamped to.</TypeRow>
<TypeRow name={"onZoomChange"} type={"(zoom, eventDetails) => void"}>Fires when the zoom changes, already clamped. `eventDetails.cancel()` keeps the zoom where it was.</TypeRow>
<TypeRow name={"position"} type={"number"}>Playback position, from 0 to `duration`.</TypeRow>
<TypeRow name={"defaultPosition"} type={"number"} default={"0"}>Uncontrolled starting position.</TypeRow>
<TypeRow name={"source"} type={"{ getSnapshot, subscribe }"}>A live position followed outside React; takes precedence over `position`.</TypeRow>
<TypeRow name={"onPositionChange"} type={"(value, eventDetails) => void"}>Fires while the position is moved: press, drag, keyboard and input changes.</TypeRow>
<TypeRow name={"onPositionCommitted"} type={"(value, eventDetails) => void"}>Fires when a gesture that moved the position ends.</TypeRow>
<TypeRow name={"snapStep"} type={"number"} default={"0"}>Distance between grid lines, in your units. 0 is no grid.</TypeRow>
<TypeRow name={"snapOffset"} type={"number"} default={"0"}>Where the grid's lines start, in your units.</TypeRow>
<TypeRow name={"step / largeStep"} type={"number"} default={"snapStep / ten steps"}>Keyboard steps.</TypeRow>
<TypeRow name={"disabled"} type={"boolean"} default={"false"}>Disables seeking and zooming.</TypeRow>
</TypeTable>

#### Parts

<TypeTable title="Components">
<TypeRow name={"TimelineViewport"}>The scrolling window. Zooms around the pointer with Cmd/Ctrl + scroll. `scrollbar="hidden"` drops the native scrollbar.</TypeRow>
<TypeRow name={"TimelineRuler"}>The lane that seeks. Holds the input; takes `aria-label` and `getAriaValueText`.</TypeRow>
<TypeRow name={"TimelineLane"}>A strip the timeline's parts are placed on. `gridStep` draws a line every that many units.</TypeRow>
<TypeRow name={"TimelineTrack"}>A row: a header beside a lane. `height`, `selected`, `onHeightChange`.</TypeRow>
<TypeRow name={"TimelineTrackHeader"}>The strip that names a track, kept at the left edge.</TypeRow>
<TypeRow name={"TimelineTrackResizeHandle"}>Sets the track's height, by dragging or with the keys.</TypeRow>
<TypeRow name={"TimelineMark"}>Something placed at `value`: a bar number, a cue. With `onValueChange` or `onValueCommitted` it moves: a drag takes it along the lane, on the grid unless Cmd/Ctrl is held, landing on a `snapPoints` place within a few px, and Left and Right on something focused inside it move it by `step`. A press that stays one keeps its click; a drag does not click.</TypeRow>
<TypeRow name={"TimelinePlayhead"}>Follows the position on the lane it sits in.</TypeRow>
<TypeRow name={"TimelineRange"}>A stretch of the timeline: `value`, `defaultValue`, `minLength`, `movable`, `drawable`, `onValueChange`, `onValueCommitted`.</TypeRow>
<TypeRow name={"TimelineRangeHandle"}>Resizes one `edge` of the range.</TypeRow>
<TypeRow name={"TimelineClip"}>A stretch of a source: `value`, `sourceLength`, `selected`, `onValueChange`, `onValueCommitted`, `onRemove`. Dragged over other lanes too.</TypeRow>
<TypeRow name={"TimelineClipHeader"}>The clip's title bar: drag it to move the clip.</TypeRow>
<TypeRow name={"TimelineClipHandle"}>Trims one `edge` of the clip.</TypeRow>
<TypeRow name={"TimelineClipFade"}>Draws a fade, as a line with its shading. Takes `edge`.</TypeRow>
<TypeRow name={"TimelineClipFadeHandle"}>Sets a fade, at the clip's top corner: a square that shows when the clip is hovered or selected. Takes `edge`.</TypeRow>
<TypeRow name={"TimelineNavigator"}>The whole timeline as one bar, scrolling and zooming the viewport.</TypeRow>
</TypeTable>

`@audio-ui/react/timeline` exposes the same parts as `Timeline.Root`, `Timeline.Viewport`, `Timeline.Ruler`, `Timeline.Lane`, `Timeline.Mark`, `Timeline.Playhead`, `Timeline.Range`, `Timeline.RangeHandle`, `Timeline.Clip`, `Timeline.ClipHeader`, `Timeline.ClipHandle`, `Timeline.ClipFade`, `Timeline.ClipFadeHandle`, and `Timeline.Navigator` with its `Timeline.NavigatorWindow` and `Timeline.NavigatorHandle`. Every part accepts a `render` prop and `className` / `style` as functions of state, and exposes `data-disabled`, `data-fitted` and `data-scrubbing`.

### ArrangedClip

<TypeTable>
<TypeRow name={"clip"} type={"Arranged"}>The clip: its place (`value`), its source and its notes.</TypeRow>
<TypeRow name={"selected"} type={"boolean"}>Whether it is in the selection.</TypeRow>
<TypeRow name={"swatch"} type={"number"}>Its color, an index into `TRACK_COLORS`: its track's.</TypeRow>
<TypeRow name={"peaks"} type={"number[]"}>An audio clip's source as peaks from 0 to 1, spread evenly over its `sourceLength`: its waveform is drawn from them, the stretch it plays, round its loop, mirrored when reversed, stretched by its warp (`clipWaveform` in `lib/arrangement` reads it the same way).</TypeRow>
<TypeRow name={"trackName"} type={"string"}>Its track's name, for its label.</TypeRow>
<TypeRow name={"describe"} type={"(time) => string"}>A time in words, such as "Bar 3, beat 2".</TypeRow>
<TypeRow name={"onChange, onCommit"} type={"(id, value, details) => void, (id, details) => void"}>The clip moved, trimmed or faded, and the gesture ended.</TypeRow>
<TypeRow name={"onSelect"} type={"(id, additive) => void"}>It was pressed: `additive` with Shift, Cmd or Ctrl, or with `multi`.</TypeRow>
<TypeRow name={"onAction"} type={"(action, id) => void"}>Picked from its menu: `"edit"`, `"duplicate"`, `"split"`, `"toggle"` or `"remove"`.</TypeRow>
<TypeRow name={"onRename, onRemove, onFadeShape"} type={"(id, …) => void"}>Renamed in place, deleted with its key, and a fade's curve picked.</TypeRow>
<TypeRow name={"multi"} type={"boolean"} default={"false"}>Whether a press adds it to the selection, for a touch screen.</TypeRow>
<TypeRow name={"copying, lift, onLift"} type={"boolean, number, (id, offset) => void"}>For group drags: whether it is a copy being dragged, how far it is held up or down, and how far it is dragged.</TypeRow>
</TypeTable>

### useArrangedClips

<TypeTable>
<TypeRow name={"clips, copying, selectedIds, tracks, dispatch"} type={"as useArrangement returns them"}>The arrangement. A return track takes no clip: leave it out of `tracks`.</TypeRow>
<TypeRow name={"getPosition, splitStep"} type={"() => number, number"}>Where the menu's split cuts, and the closest it comes to a clip's edge.</TypeRow>
<TypeRow name={"viewportRef"} type={"Ref"}>The viewport the tracks scroll in: a clip let go off them goes to the nearest.</TypeRow>
<TypeRow name={"onEdit, onPress"} type={"(id) => void, () => void"}>"Edit" chosen from a clip's menu, and a clip pressed, for what else it lets go of.</TypeRow>
<TypeRow name={"clipProps(clip), ghostsOn(trackId)"} type={"props, Arranged[]"}>What an `ArrangedClip` takes, and the originals of the clips being copied on a track.</TypeRow>
<TypeRow name={"onRemove, onRename"} type={"(id) => void, (id, name) => void"}>The same commands, for a clip's other editors.</TypeRow>
</TypeTable>

### useTrackReorder

<TypeTable>
<TypeRow name={"order"} type={"string[]"}>The tracks' ids, top to bottom.</TypeRow>
<TypeRow name={"onMove"} type={"(id, index) => void"}>A track was dropped at `index`.</TypeRow>
<TypeRow name={"gripProps(id)"} type={"props"}>For a track's grip: its pointer and key handlers.</TypeRow>
<TypeRow name={"draggingId, drop"} type={"string | null, { id, edge } | null"}>The track dragged, and where it would land.</TypeRow>
<TypeRow name={"ghost, ghostRef"} type={"{ id, top, left, width, height } | null, Ref"}>The copy to draw under the pointer, and the ref it takes.</TypeRow>
<TypeRow name={"TrackGrip"} type={"dragging, trackName, ...gripProps(id)"}>The grip, down the left edge of a track's header.</TypeRow>
<TypeRow name={"TrackReorderGhost"} type={"ghost, ghostRef, trackName"}>The copy of the dragged track's header under the pointer.</TypeRow>
<TypeRow name={"trackReorderClass(reorder, id)"} type={"string"}>A row's classes while a track is dragged: faded where it was, a line where it would land.</TypeRow>
</TypeTable>

### TimelineLocator and TimelineSignature

<TypeTable>
<TypeRow name={"locator"} type={"{ at, name }"}>A locator.</TypeRow>
<TypeRow name={"onJump, onRemove, onRename"} type={"(at) => void, (at) => void, (at, name) => void"}>Pressed, deleted and renamed.</TypeRow>
<TypeRow name={"change"} type={"{ id, at, beatsPerBar, beatUnit }"}>A time-signature change.</TypeRow>
<TypeRow name={"editing, onEditingChange"} type={"boolean, (id | null) => void"}>Whether it is being typed.</TypeRow>
<TypeRow name={"onMove, onSet, onRemove"} type={"(id, to) => void, (id, beats, unit) => void, (id) => void"}>Moved by `step` with the arrows, typed, deleted.</TypeRow>
<TypeRow name={"step"} type={"number"}>How far the arrows move a change: the grid.</TypeRow>
</TypeTable>

### useTimelineMarkers

<TypeTable>
<TypeRow name={"dispatch, getPosition"} type={"(command) => state, () => number"}>The arrangement's commands, and where the cursor is.</TypeRow>
<TypeRow name={"onJump, onPlay, step"} type={"(at) => void, (at) => void, number"}>A marker pressed, a locator double-clicked to play from, and how far the arrows move one.</TypeRow>
<TypeRow name={"locator, signature"} type={"props"}>To spread on `TimelineLocator` and `TimelineSignature`.</TypeRow>
<TypeRow name={"addLocator, addSignature, editingSignature"} type={"() => void, () => void, string | null"}>New markers at the cursor, and the change being typed.</TypeRow>
</TypeTable>

### useTimelineCursor

<TypeTable>
<TypeRow name={"clips, tracks, locators, selectedIds, selectedTracks, duration"} type={"as useArrangement returns them"}>What the keys move through.</TypeRow>
<TypeRow name={"getPosition, step"} type={"() => number, number"}>Where the cursor is, and the grid it moves on.</TypeRow>
<TypeRow name={"onSeek, onPickTrack, onAddLocator"} type={"(seconds) => void, (id) => void, () => void"}>What the keys ask for.</TypeRow>
<TypeRow name={"handleKey, handleArrowKey"} type={"(event) => boolean"}>M and Alt with the arrows; the plain arrows, Home and End.</TypeRow>
</TypeTable>

### useTimeSelection

<TypeTable>
<TypeRow name={"bar"} type={"number"}>How long a stretch Cmd/Ctrl + I opens with nothing selected, in seconds.</TypeRow>
<TypeRow name={"clips, tracks, selectedIds, selectedTracks"} type={"as useArrangement returns them"}>What a selection and a paste read.</TypeRow>
<TypeRow name={"copyOf, dispatch"} type={"(time) => Clipping, (command) => void"}>From `useArrangement`.</TypeRow>
<TypeRow name={"getPosition"} type={"() => number"}>Where the cursor is, in seconds.</TypeRow>
<TypeRow name={"time, setTime, clear"} type={"{ range, tracks } | null, (time) => void, () => void"}>The stretch of time selected.</TypeRow>
<TypeRow name={"pressLane, onSelectionChange"} type={"(trackId | null) => void, for TimelineLane"}>A press on bare lane, and the drag from it.</TypeRow>
<TypeRow name={"handleKey"} type={"(event) => boolean"}>Runs the command a key asks for, and says whether there was one.</TypeRow>
</TypeTable>

### useTrackAutomation

<TypeTable>
<TypeRow name={"dispatch, tracks"} type={"(command) => void, ArrangementTrack[]"}>The arrangement's commands, and the tracks whose automation a change by hand overrides.</TypeRow>
<TypeRow name={"automating, setAutomating, toggle"} type={"boolean, (on) => void, () => void"}>Automation mode.</TypeRow>
<TypeRow name={"parameterOf, show"} type={"(trackId) => parameter, (trackId, parameter) => void"}>What a track's lane shows, Volume until another is chosen.</TypeRow>
<TypeRow name={"changeMix"} type={"(trackId, patch) => void"}>A mix changed by hand: a fader's drag is a burst, a switch a step, and an automated gain or pan is overridden.</TypeRow>
<TypeRow name={"overridden, anyOverridden, reenable"} type={"Record<trackId, parameter[]>, boolean, () => void"}>What plays its mix instead of its envelope, and the way back.</TypeRow>
<TypeRow name={"edit, clear"} type={"(trackId, parameter, points) => void, (trackId, parameter) => void"}>Draws an envelope, which plays again; takes one away.</TypeRow>
</TypeTable>

### TrackAutomationChooser and TrackAutomation

<TypeTable>
<TypeRow name={"track"} type={"ArrangementTrack"}>The track, with its `automation`.</TypeRow>
<TypeRow name={"parameter"} type={"\"volume\" | \"pan\""}>What the lane shows.</TypeRow>
<TypeRow name={"onParameterChange, onClear"} type={"(trackId, parameter) => void"}>The chooser's: another parameter picked, and its envelope cleared.</TypeRow>
<TypeRow name={"mix"} type={"Mix"}>The track's mix, where a line with no points stands.</TypeRow>
<TypeRow name={"onChange, onCommit"} type={"(trackId, parameter, points) => void, () => void"}>The envelope was edited, and the gesture ended.</TypeRow>
<TypeRow name={"duration, snapStep, describe"} type={"number, number, (time) => string"}>The arrangement's length, the grid a point snaps to in time, and a time in words.</TypeRow>
<TypeRow name={"overridden"} type={"(\"volume\" | \"pan\")[]"}>The parameters moved by hand since, drawn greyed.</TypeRow>
</TypeTable>

### useMidiPlayback

<TypeTable>
<TypeRow name={"clips, tracks"} type={"Arranged[], ArrangementTrack[]"}>The arrangement.</TypeRow>
<TypeRow name={"clock"} type={"Source<number>"}>Where the arrangement plays, in its time.</TypeRow>
<TypeRow name={"playing"} type={"boolean"}>Whether it plays.</TypeRow>
<TypeRow name={"loop, looping"} type={"[start, end], boolean"}>The loop, which notes are scheduled up to.</TypeRow>
<TypeRow name={"audible"} type={"(trackId) => boolean"}>Whether a track is heard: not muted, and in the solo if there is one.</TypeRow>
<TypeRow name={"levelsAt"} type={"(trackId, time) => { gain, pan }"}>A track's gain and pan at a time.</TypeRow>
<TypeRow name={"returns"} type={"{ onNoteOn, onNoteOff }"}>For a MIDI clip's editor.</TypeRow>
</TypeTable>
